import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Link, type SxProps, type Theme } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useGetResolverJobDisplayName } from "../../../../hooks/useJobDisplayName";
import { FONT_SIZE } from "../../../../theme/tokens";
import SystemBanner from "../../../UIToolkit/molecules/SystemBanner";
import { buildTeamTabUrl, TEAM_TAB_PARAM } from "../../teamTabDeepLink";
import { usePageScheduleHolidays } from "./scheduleHolidaysContext";
import { holidayJobLines, scheduleHolidayBannerTitle, scheduleHolidayChangedLine, scheduleHolidayStatusLine } from "./scheduleHolidayText";

type Props = {
	date: DateTime;
	/** The link to Team → Holidays; the mobile schedule leaves it out. */
	showViewHolidays: boolean;
	/** Placement of the box around the banner, which renders only when there is something to say. */
	sx: SxProps<Theme>;
	dataTestId: string;
};

/**
 * The Day view's note on a holiday: who works it, each job's hours, and whether it is on the schedule. A warning
 * instead when the holidays couldn't load; nothing on a day without one.
 */
export const HolidayDayBanner: FC<Props> = ({ date, showViewHolidays, sx, dataTestId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment, timezone } = useHospitalData();
	const jobName = useGetResolverJobDisplayName();
	const { holidayOn, isError } = usePageScheduleHolidays();
	const scheduleHoliday = holidayOn(date);
	if (isError) {
		return (
			<Box sx={sx}>
				<SystemBanner tone="warning" icon={<WarningAmberRoundedIcon />} title={t("holidays.schedule.banner.loadError")} dataTestId={`${dataTestId}-error`} />
			</Box>
		);
	}
	if (scheduleHoliday === undefined) return null;
	const changedLine = scheduleHolidayChangedLine(t, scheduleHoliday);
	return (
		<Box sx={sx}>
			<SystemBanner
				tone="primary"
				emphasis={scheduleHoliday.applied ? "strong" : "pending"}
				icon={<EventOutlinedIcon />}
				title={scheduleHolidayBannerTitle(t, scheduleHoliday)}
				descriptionComponent="div"
				description={
					<>
						{holidayJobLines(t, scheduleHoliday.holiday.windows, jobName, timezone).map((line) => (
							<Box key={line.jobIds.join(",")} data-testid={`${dataTestId}-job-line`}>
								{line.text}
							</Box>
						))}
						<Box data-testid={`${dataTestId}-status`}>{scheduleHolidayStatusLine(t, scheduleHoliday)}</Box>
						{changedLine !== null && <Box data-testid={`${dataTestId}-changed`}>{changedLine}</Box>}
					</>
				}
				action={
					// A real link, not a button: it opens another page, so it can open in a new tab and shows where it goes.
					showViewHolidays && (
						<Link
							component={RouterLink}
							to={buildTeamTabUrl(chosenDepartment.department_id, TEAM_TAB_PARAM.HOLIDAYS)}
							onClick={() =>
								track("schedule_holiday_view_holidays_clicked", { department_id: chosenDepartment.department_id, holiday_id: scheduleHoliday.holiday.id })
							}
							underline="hover"
							sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: "fontWeightMedium", whiteSpace: "nowrap" }}
							data-testid={`${dataTestId}-view-holidays`}
						>
							{t("holidays.schedule.banner.viewHolidays")}
						</Link>
					)
				}
				dataTestId={dataTestId}
			/>
		</Box>
	);
};
